import pluralize from 'pluralize'
import {
  type JSX,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { usePrevious } from '~/hooks'

import {
  AnalyticsQueryColumn,
  AnalyticsQueryFilter,
  FilterCollection,
  getValueAsArray,
} from '@hap/analytics'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  IconTypes,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@hap/components'
import {
  NEGATIVE_SENTIMENT_LABEL_ID,
  NEUTRAL_SENTIMENT_LABEL_ID,
  POSITIVE_SENTIMENT_LABEL_ID,
} from '@hap/constants'
import { humanizeSecondsToMinutes, toPercent } from '@hap/utils'

import { useDatastore } from '../datastore'
import { ALIAS_FIELDS, AVAILABLE_FILTERS, DEFAULT_FILTER_OPERATORS } from './constants'
import { FilterCategorySelector } from './filter-category-selector'
import { FilterContext } from './filter-context'
import { ChannelFilterControl } from './record-types/channel'
import {
  ScorecardCriteriaFilterControl,
  ScorecardCriteriaFilterTrigger,
} from './record-types/criteria'
import { CustomerReactionsFilterControl } from './record-types/customer-reactions'
import { DnisFilterControl } from './record-types/dnis'
import { FlagFilterControl } from './record-types/flag'
import { GroupFilterControl } from './record-types/groups'
import { IntentFilterControl } from './record-types/intent'
import { MethodFilterControl } from './record-types/method'
import { ScorecardFilterControl } from './record-types/scorecard'
import { TagFilterControl } from './record-types/tag'
import { UserFilterControl } from './record-types/user'
import { WorkflowFilterControl } from './record-types/workflow'
import { WorkspaceFilterControl } from './record-types/workspace'
import { DirectionFilterControl } from './static-types/direction'
import { DurationFilterControl } from './static-types/duration'
import { ExternalIdFilterControl } from './static-types/external-id'
import { MessageFilterControl } from './static-types/messages'
import { PercentageFilterControl } from './static-types/percentage'
import { PiiFilterControl } from './static-types/pii'

export const getCollectionFromField = (field: string) => {
  switch (field) {
    case 'channelId':
      return {
        params: { query: '', collection: 'contactChannels' },
      }
    // @deprecated use handledByIds
    case 'handledBy':
      return {
        params: { query: '', collection: 'users' },
      }
    case 'handledByIds':
      return {
        params: { query: '', collection: 'users' },
      }

    case 'intentId':
      return {
        collection: 'labels',
        params: { query: '', category: 'Intent' },
      }
    default:
      return {
        collection: '',
        params: {},
      }
  }
}
const filterMap: Record<
  'in' | 'nin' | 'gte' | 'lte' | 'between',
  {
    label: {
      singular: string
      plural: string
    }
    icon: IconTypes
  }
> = {
  in: {
    label: {
      singular: 'is',
      plural: 'is any of',
    },
    icon: 'plus',
  },
  nin: {
    label: {
      singular: 'is not',
      plural: 'is not',
    },
    icon: 'prohibit',
  },
  gte: {
    label: {
      singular: 'greater or equal',
      plural: 'greater or equal',
    },
    icon: 'greater-than-or-equal',
  },
  lte: {
    label: {
      singular: 'less or equal',
      plural: 'less or equal',
    },
    icon: 'less-than-or-equal',
  },
  between: {
    label: {
      singular: 'is between',
      plural: 'is between',
    },
    icon: 'arrows-horizontal',
  },
} as const

export const mapFieldToLabel = (field?: string | null) => {
  switch (field) {
    case 'direction':
      return FilterCollection.Direction
    case 'channelId':
      return FilterCollection.Channels
    case 'flagId':
      return FilterCollection.Flags
    case 'groupIds':
      return FilterCollection.Groups
    case 'handledBy':
    case 'handledByIds':
      return FilterCollection.Users
    case 'intentId':
      return FilterCollection.Labels
    case 'method':
      return FilterCollection.Methods
    case 'piiIds':
      return FilterCollection.PII
    case 'scorecardId':
      return FilterCollection.Scorecards
    case 'scorecardCriteriaId':
      return FilterCollection.ScorecardCriteria
    case 'tagIds':
      return FilterCollection.Tags
    case 'to':
      return FilterCollection.Dnis
    case 'workspaceId':
      return FilterCollection.Workspaces
    case 'workflows':
      return FilterCollection.Workflows

    case 'initialCustomerReactionIds':
    case 'medialCustomerReactionIds':
    case 'closingCustomerReactionIds':
      return FilterCollection.CustomerReactions

    default:
      return null
  }
}

export const Filter = (
  props: PropsWithChildren<{
    filter?: AnalyticsQueryFilter
    onSelect: (updates: AnalyticsQueryFilter) => void
    selectableFilters?: AnalyticsQueryColumn[]
    contextFilters?: {
      [key: string]: {
        $eq: string[]
      }
    }
  }>,
): JSX.Element => {
  const filter = props.filter
  const [open, setOpen] = useState(false)
  const [field, setField] = useState(filter?.field || null)
  const previousField = usePrevious(filter?.field)
  const handleOpenChange = useCallback(
    (open: boolean) => {
      setOpen(open)
      if (!open && !filter) {
        setField(null)
      }
    },
    [filter],
  )

  useEffect(() => {
    if (previousField !== filter?.field) {
      setField(filter?.field || null)
    }
  }, [filter?.field, previousField])

  return (
    <FilterContext.Provider
      value={{
        filter,
        field,
        setField,
        setOpen,
        open,
        onSelect: props.onSelect,
        selectableFilters: props.selectableFilters,
        contextFilters: props.contextFilters,
      }}
    >
      <Popover open={open} onOpenChange={handleOpenChange}>
        {props.children}
      </Popover>
    </FilterContext.Provider>
  )
}

pluralize.addIrregularRule('is', 'has')
pluralize.addIrregularRule('is not', 'has not')

export const FilterTrigger = (props: { onDelete: () => void; readOnly?: boolean }) => {
  const { filter, onSelect } = useContext(FilterContext)

  if (!filter || 'compound' in filter) {
    return filter?.field === 'scorecardCriteriaId' ? (
      <ScorecardCriteriaFilterTrigger
        readOnly={props.readOnly}
        onDelete={props.onDelete}
      />
    ) : null
  }

  const { label, operation, operators, values, richValues } = useHumanizeFilter(filter)
  const showsValue =
    !ALIAS_FIELDS.includes(filter.field) &&
    !['isNotNull', 'isNull'].includes(filter.op) &&
    filter.field !== 'hasDispute'

  if (props.readOnly) {
    return (
      <div className="border-input bg-secondary text-secondary-foreground flex items-center overflow-hidden rounded-md border opacity-70">
        <div className={cn('px-2 py-1.5 text-nowrap', showsValue && 'border-r')}>
          {label}
        </div>
        {showsValue && (
          <>
            <div className="border-r px-3 py-1.5 text-nowrap">{operation}</div>
            <div className="px-2 py-1.5 text-nowrap">{richValues}</div>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="bg-accent flex items-stretch rounded-lg p-1 pr-0">
      <div className="bg-background border-border-dark flex items-center gap-1 rounded-l-md border bg-linear-to-t px-2 text-nowrap">
        {label}
      </div>
      {showsValue && (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger className="bg-background border-border-dark border-t border-b px-2">
              {operation}
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {Object.entries(filterMap).map(([op, label]) => {
                if (!operators.includes(op)) return
                return (
                  <DropdownMenuItem
                    key={op}
                    onSelect={() => {
                      if (filter) {
                        if (['gt', 'gte', 'lte', 'lt'].includes(op)) {
                          onSelect({
                            op: op as 'gt' | 'gte' | 'lte' | 'lt',
                            field: filter.field,
                            value: `${values[0]}`,
                          })
                        } else {
                          onSelect({
                            op: op as 'nin' | 'in' | 'between',
                            field: filter.field,
                            values: values,
                          })
                        }
                      }
                    }}
                    className="flex items-center gap-3"
                  >
                    <Icon name={label.icon} />
                    {values.length === 1 ? label.label.singular : label.label.plural}
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          <PopoverTrigger className="bg-background border-border-dark rounded-r-md border px-2">
            {richValues}
          </PopoverTrigger>
        </>
      )}

      <Button onClick={props.onDelete} variant="ghost" className="px-2" icon="x" />
    </div>
  )
}
export const NewFilterTrigger = (props: { condensed?: boolean }) => {
  return (
    <Tooltip>
      <PopoverTrigger asChild>
        <TooltipTrigger asChild>
          <Button
            title="Filter"
            variant="ghost"
            icon="funnel-simple"
            size={props.condensed ? 'icon' : undefined}
          >
            {props.condensed != true ? 'Filter' : null}
          </Button>
        </TooltipTrigger>
      </PopoverTrigger>
      <TooltipContent>Filter</TooltipContent>
    </Tooltip>
  )
}

export function FilterContent(): JSX.Element {
  const { setField, field } = useContext(FilterContext)
  const renderContent = useCallback((field: Nullable<AnalyticsQueryFilter['field']>) => {
    switch (field) {
      case null:
        return <FilterCategorySelector />
      case 'averageEvaluationScore':
        return <PercentageFilterControl />
      case 'escalationRiskScore':
        return <PercentageFilterControl field="escalationRiskScore" />
      case 'channelId':
        return <ChannelFilterControl />
      case 'flagId':
        return <FlagFilterControl />
      case 'groupIds':
        return <GroupFilterControl />
      case 'handledBy':
      case 'handledByIds':
        return <UserFilterControl placeholder="Handled by" />
      case 'assignedSupervisorId':
        return (
          <UserFilterControl
            placeholder="Reports to"
            contextFilters={{
              subordinates: {
                $exists: true,
              },
            }}
          />
        )
      case 'handleTime':
        return <DurationFilterControl />
      case 'recordingDuration':
        return <DurationFilterControl field="recordingDuration" />
      case 'wrapTime':
        return <DurationFilterControl field="wrapTime" />
      case 'talkTime':
        return <DurationFilterControl field="talkTime" />
      case 'intentId':
        return <IntentFilterControl />
      case 'isOutbound':
        return <DirectionFilterControl />
      case 'method':
        return <MethodFilterControl />
      case 'messageCount':
        return <MessageFilterControl />
      case 'scorecardCriteriaId':
        return <ScorecardCriteriaFilterControl />
      case 'scorecardId':
        return <ScorecardFilterControl />
      case 'tagIds':
        return <TagFilterControl />
      case 'to':
        return <DnisFilterControl />
      case 'piiIds':
        return <PiiFilterControl />
      case 'workflowId':
        return <WorkflowFilterControl />
      case 'workspaceId':
        return <WorkspaceFilterControl />
      case 'externalId':
        return <ExternalIdFilterControl />

      case 'initialCustomerReactionIds':
      case 'medialCustomerReactionIds':
      case 'closingCustomerReactionIds':
        return <CustomerReactionsFilterControl />
    }
  }, [])

  useHotkeys('ArrowLeft', () => setField(null), { enableOnFormTags: ['INPUT'] })

  return (
    <PopoverContent className="w-[300px] p-0 will-change-auto">
      {renderContent(field)}
    </PopoverContent>
  )
}

export function useHumanizeFilter(filter: AnalyticsQueryFilter) {
  const values = getValueAsArray(filter)
  const dataStore = mapFieldToLabel(filter?.field)
  const { data } = useDatastore(dataStore)

  const usesDataStore = dataStore !== null
  const friendly = AVAILABLE_FILTERS[filter.field]
  const operation = filterMap[filter.op as 'in' | 'nin' | 'gte' | 'lte' | 'between']

  const label = useMemo(() => {
    if (
      ['v2_overallSentimentId', 'overallCustomerSentimentLabelId'].includes(
        filter.field,
      ) &&
      filter.op === 'in'
    ) {
      if (filter.values[0] === NEGATIVE_SENTIMENT_LABEL_ID)
        return (
          <>
            <Icon name="smiley-angry" /> Overall Negative Sentiment
          </>
        )
      if (filter.values[0] === POSITIVE_SENTIMENT_LABEL_ID)
        return (
          <>
            <Icon name="smiley" /> Overall Positive Sentiment
          </>
        )

      if (filter.values[0] === NEUTRAL_SENTIMENT_LABEL_ID)
        return (
          <>
            <Icon name="smiley-meh" /> Overall Neutral Sentiment
          </>
        )

      return 'something'
    }
    if (filter.field === 'deadAirCount' && filter.op === 'gt') {
      return (
        <>
          <Icon name="ear-slash" /> Has Dead Air
        </>
      )
    }
    if (filter.field === 'reviewedAt' && filter.op === 'isNotNull') {
      return (
        <>
          <Icon name="user-circle-check" />
          Reviewed
        </>
      )
    }
    if (filter.field === 'reviewedAt' && filter.op === 'isNull') {
      return (
        <>
          <Icon name="tray" />
          Pending Review
        </>
      )
    }
    if (filter.field === 'agentOvertalkOccurrences' && filter.op === 'gt') {
      return (
        <>
          <Icon name="ear-slash" />
          Has Agent Overtalk
        </>
      )
    }
    if (
      filter.field === 'averageEvaluationScore' &&
      ['isNull', 'isNotNull'].includes(filter.op)
    ) {
      return (
        <>
          <Icon name="medal" />
          {filter.op === 'isNotNull' ? 'Has Been Evaluated' : 'Has Not Been Evaluated'}
        </>
      )
    }
    if (filter.field === 'hasAutoEvaluation' && filter.op === 'eq') {
      return (
        <>
          <Icon name="sparkle" />
          {filter.value === true
            ? 'Has Auto Evaluation'
            : 'Does Not Have Auto Evaluation'}
        </>
      )
    }
    if (filter.field === 'hasManualEvaluation' && filter.op === 'eq') {
      return (
        <>
          <Icon name="barbell" />
          {filter.value === true
            ? 'Has Manual Evaluation'
            : 'Does Not Have Manual Evaluation'}
        </>
      )
    }
    if (filter.field === 'hasDispute' && filter.op === 'eq') {
      return (
        <>
          <Icon name="flag" />
          {filter.value === true ? 'Has Dispute' : 'Does Not Have Dispute'}
        </>
      )
    }
    if (filter.field === 'hasEscalation' && filter.op === 'eq') {
      return (
        <>
          <Icon name="warning-circle" />
          {filter.value === true ? 'Has Escalation' : 'Does Not Have Escalation'}
        </>
      )
    }
    if (filter.field === 'isVoicemail' && filter.op === 'eq') {
      return (
        <>
          <Icon name="cassette-tape" />
          {filter.value === true ? 'Is Voicemail' : 'Is Not Voicemail'}
        </>
      )
    }
    if (filter.field === 'score' && filter.op === 'isNotNull') {
      return 'Has Been Evaluated'
    }
    if (filter.field === 'score' && filter.op === 'isNull') {
      return 'Has Not Been Evaluated'
    }
    if (filter.field === 'metHandleTimeGoal' && filter.op === 'eq') {
      return (
        <>
          <Icon name="timer" />
          {filter.value === 'true' ? 'Met Handle Time Goal' : 'Missed Handle Time Goal'}
        </>
      )
    }
    if (filter.field === 'metDeadAirGoal' && filter.op === 'eq') {
      return (
        <>
          <Icon name="ear-slash" />
          {filter.value === 'true' ? 'Met Dead Air Goal' : 'Missed Dead Air Goal'}
        </>
      )
    }

    if (filter.field === 'isVoicemail' && filter.op === 'eq') {
      return (
        <>
          <Icon name="ear-slash" />
          {filter.value === 'true' ? 'Is Voicemail' : 'Is Not Voicemail'}
        </>
      )
    }

    if (filter.field === 'score' && filter.op === 'isNotNull') {
      return 'Has Been Evaluated'
    }

    if (filter.field === 'lastCommentedAt' && filter.op === 'isNotNull') {
      return (
        <>
          <Icon name="chat-circle-dots" />
          Has Comments
        </>
      )
    }

    if (friendly) {
      return (
        <>
          <Icon name={friendly.icon} weight={friendly.iconWeight} /> {friendly.name}
        </>
      )
    }
    return filter.field
  }, [filter.field, filter.op])

  const richValues = useMemo(() => {
    if (values.length === 0) return <i className="text-accent-foreground">No selection</i>

    if (usesDataStore && values.length === 1) {
      return (
        data.find((f) => f.id === values[0])?.name || (
          <Skeleton className="min-w-[100px]" loading />
        )
      )
    }

    if (friendly?.filterCollection === 'direction') {
      return values.map((v) => (v ? 'Outbound' : 'Inbound')).join(', ')
    }

    if (friendly?.filterCollection === 'duration') {
      return values.map((v) => humanizeSecondsToMinutes(v as string)).join(' and ')
    }

    if (friendly?.filterCollection === 'averageEvaluationScore') {
      return values.map((v) => toPercent(v as unknown as string)).join(' and ')
    }

    if (friendly?.filterCollection === 'messageCount') {
      return values.map((v) => v as unknown as string).join(' and ')
    }

    return `${values.length}${friendly?.suffix ? ` ${friendly.suffix}` : ''}`
  }, [data, values, friendly, usesDataStore])

  return {
    label,
    operators: friendly?.operators || DEFAULT_FILTER_OPERATORS,
    operation: operation
      ? values.length === 1
        ? operation.label.singular
        : operation.label.plural
      : '',
    values,
    richValues,
  }
}
